import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MapPin, Flame, TrendingUp, ChevronRight, Info, FilterX, Eye } from 'lucide-react';
import { geoAnalyticsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const GREEN = '#1B5C3A';
const GOLD = '#F5A623';
const PURPLE = '#4C1D95';
const BLUE = '#2563eb';
const RED = '#dc2626';
const CREAM = '#FAF9F6';
const CHAR = '#1a1a1a';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';

const HEAT = { hot: RED, warm: GOLD, cool: GREEN };
const HEAT_BG = { hot: 'rgba(220,38,38,0.08)', warm: 'rgba(245,166,35,0.10)', cool: 'rgba(27,92,58,0.07)' };

const fmtVal = (v, metric) => {
  const n = Number(v) || 0;
  if (metric === 'leadRevenue') return '₹' + n.toLocaleString('en-IN');
  if (metric === 'opportunity') return n.toFixed(1);
  return n.toLocaleString('en-IN');
};
const kfmt = (v) => {
  const n = Number(v) || 0;
  if (n >= 1e7) return (n / 1e7).toFixed(1) + 'Cr';
  if (n >= 1e5) return (n / 1e5).toFixed(1) + 'L';
  if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
  return String(Math.round(n));
};

// ── hand-rolled SVG trend chart: actual (solid) + forecast (dashed) ─────────
function TrendChart({ series }) {
  const W = 820, H = 300, padL = 52, padR = 18, padT = 18, padB = 46;
  const n = series.length;
  const actualN = series.filter(s => !s.forecast).length;

  if (actualN === 0) {
    return <div style={{ color: MUTED, padding: '48px 20px', textAlign: 'center', fontSize: 14, fontWeight: 500 }}>No activity in this period for the current selection.</div>;
  }
  if (actualN === 1 && n === 1) {
    const s = series[0];
    return (
      <div style={{ color: MUTED, padding: '40px 20px', textAlign: 'center', fontSize: 14, fontWeight: 500 }}>
        Only one month of history so far — <b style={{ color: CHAR }}>{s.label}: {s.value.toLocaleString('en-IN')}</b>.
        A trend line appears once there are 2+ months of data.
      </div>
    );
  }

  const max = Math.max(1, ...series.map(s => s.value));
  const x = (i) => padL + (i * (W - padL - padR)) / Math.max(1, n - 1);
  const y = (v) => padT + (H - padT - padB) * (1 - v / max);
  const firstF = series.findIndex(s => s.forecast);
  const splitIdx = firstF === -1 ? n : firstF;

  const actualPts = series.slice(0, splitIdx).map((s, i) => `${x(i).toFixed(1)},${y(s.value).toFixed(1)}`).join(' ');
  const fcStart = Math.max(0, splitIdx - 1);
  const fcPts = series.slice(fcStart).map((s, k) => `${x(fcStart + k).toFixed(1)},${y(s.value).toFixed(1)}`).join(' ');

  const step = Math.max(1, Math.ceil(n / 9));
  const grid = [0, 0.25, 0.5, 0.75, 1];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} preserveAspectRatio="xMidYMid meet">
      {/* forecast region shading */}
      {firstF !== -1 && (
        <rect x={x(fcStart)} y={padT} width={W - padR - x(fcStart)} height={H - padT - padB} fill="rgba(76,29,149,0.05)" />
      )}
      {/* y grid + labels */}
      {grid.map((g, i) => {
        const gy = padT + (H - padT - padB) * (1 - g);
        return (
          <g key={i}>
            <line x1={padL} y1={gy} x2={W - padR} y2={gy} stroke={BORDER} strokeWidth="1" />
            <text x={padL - 8} y={gy + 4} textAnchor="end" fontSize="11" fill={MUTED}>{kfmt(max * g)}</text>
          </g>
        );
      })}
      {/* x labels */}
      {series.map((s, i) => (i % step === 0 || i === n - 1) ? (
        <text key={i} x={x(i)} y={H - padB + 18} textAnchor="middle" fontSize="10" fill={s.forecast ? PURPLE : MUTED}>{s.label}</text>
      ) : null)}
      {/* now divider */}
      {firstF !== -1 && (
        <g>
          <line x1={x(fcStart)} y1={padT} x2={x(fcStart)} y2={H - padB} stroke={PURPLE} strokeWidth="1.5" strokeDasharray="4 4" opacity="0.5" />
          <text x={x(fcStart) + 6} y={padT + 12} fontSize="10" fontWeight="700" fill={PURPLE}>Projection →</text>
        </g>
      )}
      {/* actual line */}
      {actualPts && <polyline points={actualPts} fill="none" stroke={GREEN} strokeWidth="2.5" strokeLinejoin="round" />}
      {/* forecast line (dashed) */}
      {firstF !== -1 && fcPts && <polyline points={fcPts} fill="none" stroke={PURPLE} strokeWidth="2.5" strokeDasharray="6 5" strokeLinejoin="round" />}
      {/* dots */}
      {series.map((s, i) => (i % step === 0 || i === n - 1) ? (
        <circle key={i} cx={x(i)} cy={y(s.value)} r="2.6" fill={s.forecast ? PURPLE : GREEN} />
      ) : null)}
    </svg>
  );
}

const SELECT_STYLE = { padding: '9px 12px', borderRadius: 9, border: `1px solid ${BORDER}`, background: '#fff', color: CHAR, fontSize: 14, fontWeight: 500, fontFamily: 'var(--font-sans)', minWidth: 160, cursor: 'pointer' };
const WINDOWS = [{ v: 'auto', t: 'Auto' }, { v: '6', t: '6m' }, { v: '12', t: '12m' }, { v: '36', t: '3y' }];

// what each lens means — shown under the metric pills so the numbers can't be misread
const METRIC_CAPTION = {
  expansion: 'Your GTM blueprint: a CATEGORY-AWARE India market baseline (the profile adapts to the selected category/sub-category/brand — Travel skews to Gujarat/Kerala, Electronics to metro states, premium brands like Apple to Mumbai/Bengaluru/Delhi) ÷ your current vendor presence. Big markets you haven’t entered rank top even with zero platform activity.',
  demandVolume: 'Real demands posted on kanjusee — shows only where the platform already has activity today. For big markets you haven’t entered yet, use Expansion Targets.',
  opportunity: 'Platform demand ÷ (active vendors + 1) — where existing buyer demand is underserved by your current vendors.',
  leadRevenue: '₹ vendors actually paid for leads (real platform data). Stays ₹0 until real lead rates are set in Lead Pricing.',
  closedDeals: 'Offers accepted by buyers (real platform data).',
};

export default function GeoAnalytics() {
  useAuth();
  const [filters, setFilters] = useState(null);
  const [sel, setSel] = useState({ categoryId: '', subCategoryId: '', brandId: '', metric: 'expansion', level: 'state', state: '', mode: 'real', window: 'auto' });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  // load filter options once
  useEffect(() => {
    geoAnalyticsAPI.filters()
      .then(r => setFilters(r.data.data || r.data))
      .catch(e => setErr(e.response?.data?.message || 'Failed to load filters'));
  }, []);

  // load report whenever selection changes
  const loadReport = useCallback(() => {
    setLoading(true);
    const p = { metric: sel.metric, level: sel.level, mode: sel.mode, months: sel.window, horizon: 24 };
    if (sel.categoryId) p.categoryId = sel.categoryId;
    if (sel.subCategoryId) p.subCategoryId = sel.subCategoryId;
    if (sel.brandId) p.brandId = sel.brandId;
    if (sel.level === 'city' && sel.state) p.state = sel.state;
    geoAnalyticsAPI.report(p)
      .then(r => { setReport(r.data.data || r.data); setErr(''); })
      .catch(e => setErr(e.response?.data?.message || 'Failed to load report'))
      .finally(() => setLoading(false));
  }, [sel]);
  useEffect(() => { loadReport(); }, [loadReport]);

  // cascading option lists
  const subOptions = useMemo(() => {
    if (!filters) return [];
    if (!sel.categoryId) return filters.subCategories;
    return filters.subCategories.filter(s => (s.categories || []).map(String).includes(String(sel.categoryId)));
  }, [filters, sel.categoryId]);
  const brandOptions = useMemo(() => {
    if (!filters) return [];
    if (!sel.subCategoryId) return filters.brands;
    return filters.brands.filter(b => (b.subCategories || []).map(String).includes(String(sel.subCategoryId)));
  }, [filters, sel.subCategoryId]);

  const set = (patch) => setSel(s => ({ ...s, ...patch }));
  const clearFilters = () => set({ categoryId: '', subCategoryId: '', brandId: '', state: '' });
  const drillCity = (area) => { if (sel.level === 'state') set({ level: 'city', state: area }); };

  if (!filters && err) return <div style={{ padding: 24 }}><div className="card" style={{ padding: 24, borderLeft: `4px solid ${RED}`, color: RED }}>{err}</div></div>;
  if (!filters) return <div style={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center', color: MUTED }}>Loading geo analytics…</div>;

  const geo = report?.geo || [];
  const maxVal = Math.max(1, ...geo.map(g => g.value || 0));
  const hotAreas = report?.hotAreas || [];
  const meta = report?.meta || {};
  const totals = report?.totals || {};
  const scope = report?.scope || {};
  const metricLabel = filters.metrics.find(m => m.key === sel.metric)?.label || sel.metric;
  const scopeText = [scope.brand, scope.subCategory, scope.category].filter(Boolean).join(' · ') || 'All inventory';
  const hasFilters = !!(sel.categoryId || sel.subCategoryId || sel.brandId || sel.state);

  const series = report?.series || [];
  const actualVals = series.filter(s => !s.forecast);
  const fcVals = series.filter(s => s.forecast);
  const dataMonths = meta.dataMonths ?? actualVals.length;

  // honest projection KPI: compare the projection with the same-length trailing actual window
  const cmpLen = Math.min(fcVals.length, actualVals.length, 12);
  const lastCmp = actualVals.slice(-cmpLen).reduce((s, r) => s + r.value, 0);
  const nextCmp = fcVals.slice(0, cmpLen).reduce((s, r) => s + r.value, 0);
  const growth = (dataMonths >= 6 && cmpLen >= 3 && lastCmp > 0) ? Math.round(((nextCmp - lastCmp) / lastCmp) * 100) : null;

  // expansion works off the market baseline, so it renders even with zero platform data
  const noRealData = sel.mode === 'real' && sel.metric !== 'expansion' && (totals.matchedDemands || 0) === 0 && !loading;

  return (
    <div style={{ padding: '32px 40px', background: CREAM, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 800, color: CHAR, margin: 0, letterSpacing: '-0.02em' }}>
            <MapPin size={26} style={{ verticalAlign: -4, marginRight: 8, color: GREEN }} />Geo Business Analytics
          </h1>
          <p style={{ color: MUTED, fontSize: 15, marginTop: 6, fontWeight: 500 }}>
            Where the business is — and where to focus vendor engagement — for <b style={{ color: CHAR }}>{scopeText}</b>.
          </p>
        </div>
        {/* Real / Demo toggle */}
        <div style={{ display: 'flex', background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden' }}>
          {['real', 'demo'].map(m => (
            <button key={m} onClick={() => set({ mode: m })} style={{
              padding: '9px 18px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-sans)',
              background: sel.mode === m ? (m === 'demo' ? PURPLE : GREEN) : 'transparent', color: sel.mode === m ? '#fff' : MUTED,
            }}>{m === 'real' ? 'Real Data' : 'Demo Mode'}</button>
          ))}
        </div>
      </div>

      {/* Data-coverage strip — always tells you what you're looking at */}
      {sel.mode === 'real' && !noRealData && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16, fontSize: 12.5, color: MUTED, fontWeight: 600 }}>
          <span style={{ padding: '4px 10px', background: 'rgba(27,92,58,0.08)', color: GREEN, borderRadius: 12 }}>
            {(totals.matchedDemands || 0).toLocaleString('en-IN')} demands matched
          </span>
          {meta.dataStart && (
            <span style={{ padding: '4px 10px', background: '#f3f4f6', borderRadius: 12 }}>
              data: {meta.dataStart} – {meta.dataEnd} ({dataMonths} mo)
            </span>
          )}
          {meta.unlocated > 0 && (
            <span style={{ padding: '4px 10px', background: HEAT_BG.warm, color: '#92650a', borderRadius: 12 }}>
              {meta.unlocated} without location
            </span>
          )}
          <span style={{ padding: '4px 10px', background: 'rgba(76,29,149,0.07)', color: PURPLE, borderRadius: 12 }}>
            projection: {meta.forecastMonths > 0 ? `${meta.forecastMonths} mo (${meta.forecastMethod})` : meta.forecastMethod}
          </span>
          {meta.segment && sel.metric === 'expansion' && (
            <span style={{ padding: '4px 10px', background: 'rgba(220,38,38,0.06)', color: RED, borderRadius: 12 }}>
              market profile: {meta.segment.label}{meta.segment.premium ? ' · premium skew' : ''} (estimate)
            </span>
          )}
        </div>
      )}

      {/* Controls */}
      <div className="card" style={{ padding: 20, marginBottom: 20, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
        <Field label="Category">
          <select style={SELECT_STYLE} value={sel.categoryId} onChange={e => set({ categoryId: e.target.value, subCategoryId: '', brandId: '' })}>
            <option value="">All categories</option>
            {filters.categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </Field>
        <ChevronRight size={18} style={{ color: BORDER, marginBottom: 10 }} />
        <Field label="Sub-category">
          <select style={SELECT_STYLE} value={sel.subCategoryId} onChange={e => set({ subCategoryId: e.target.value, brandId: '' })}>
            <option value="">All sub-categories</option>
            {subOptions.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
        </Field>
        <ChevronRight size={18} style={{ color: BORDER, marginBottom: 10 }} />
        <Field label="Brand">
          <select style={SELECT_STYLE} value={sel.brandId} onChange={e => set({ brandId: e.target.value })}>
            <option value="">All brands</option>
            {brandOptions.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
          </select>
        </Field>
        <div style={{ flex: 1 }} />
        {sel.level === 'city' && (
          <Field label="State (drill)">
            <select style={SELECT_STYLE} value={sel.state} onChange={e => set({ state: e.target.value })}>
              <option value="">Top cities (all India)</option>
              {filters.states.map(s => <option key={s.state} value={s.state}>{s.state}</option>)}
            </select>
          </Field>
        )}
        <Field label="History window">
          <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 9, padding: 3 }}>
            {WINDOWS.map(w => (
              <button key={w.v} onClick={() => set({ window: w.v })} title={w.v === 'auto' ? 'Fit the window to the months that actually have data' : undefined} style={{
                padding: '7px 12px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: sel.window === w.v ? '#fff' : 'transparent', color: sel.window === w.v ? CHAR : MUTED,
                boxShadow: sel.window === w.v ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}>{w.t}</button>
            ))}
          </div>
        </Field>
        <Field label="Geography">
          <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: 9, padding: 3 }}>
            {['state', 'city'].map(l => (
              <button key={l} onClick={() => set({ level: l, state: l === 'state' ? '' : sel.state })} style={{
                padding: '7px 14px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                background: sel.level === l ? '#fff' : 'transparent', color: sel.level === l ? CHAR : MUTED,
                boxShadow: sel.level === l ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              }}>{l === 'state' ? 'States' : 'Cities'}</button>
            ))}
          </div>
        </Field>
      </div>

      {/* Metric pills */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        {filters.metrics.map(m => (
          <button key={m.key} onClick={() => set({ metric: m.key })} style={{
            padding: '9px 16px', borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-sans)',
            border: `1.5px solid ${sel.metric === m.key ? GREEN : BORDER}`,
            background: sel.metric === m.key ? GREEN : '#fff', color: sel.metric === m.key ? '#fff' : MUTED,
          }}>{m.label}</button>
        ))}
      </div>
      {METRIC_CAPTION[sel.metric] && (
        <p style={{ fontSize: 12.5, color: MUTED, margin: '-8px 0 20px', fontWeight: 500, maxWidth: 900, lineHeight: 1.5 }}>
          {METRIC_CAPTION[sel.metric]}
          {sel.metric === 'expansion' && (
            <a href="/admin/market-profiles" style={{ marginLeft: 8, color: PURPLE, fontWeight: 700, textDecoration: 'none' }}>Tune market weights →</a>
          )}
        </p>
      )}

      {/* note banner */}
      {meta.note && !noRealData && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '12px 16px', marginBottom: 20, borderRadius: 10, background: sel.mode === 'demo' ? HEAT_BG.warm : 'rgba(37,99,235,0.06)', border: `1px solid ${sel.mode === 'demo' ? GOLD : BLUE}`, color: CHAR, fontSize: 13.5, fontWeight: 500 }}>
          <Info size={18} style={{ color: sel.mode === 'demo' ? GOLD : BLUE, flexShrink: 0 }} />{meta.note}
        </div>
      )}

      {loading && <div style={{ color: MUTED, fontSize: 13, marginBottom: 12 }}>Updating…</div>}
      {err && <div className="card" style={{ padding: 16, borderLeft: `4px solid ${RED}`, color: RED, marginBottom: 20 }}>{err}</div>}

      {/* Zero-state: explain WHY it's empty + one-click fixes */}
      {noRealData ? (
        <div className="card" style={{ padding: '48px 32px', textAlign: 'center' }}>
          <MapPin size={40} style={{ color: MUTED, opacity: 0.4 }} />
          <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: CHAR, margin: '14px 0 8px' }}>
            No demands match this selection
          </h3>
          <p style={{ fontSize: 14, color: MUTED, maxWidth: 520, margin: '0 auto 6px', fontWeight: 500, lineHeight: 1.6 }}>
            {hasFilters
              ? <>The current filter combination (<b style={{ color: CHAR }}>{scopeText}{sel.state ? ` in ${sel.state}` : ''}</b>) has no posted demands yet — that's why this page is empty, not a data problem.</>
              : <>No demands have been posted on the platform in this window yet.</>}
          </p>
          <p style={{ fontSize: 13, color: MUTED, margin: '0 auto 22px', fontWeight: 500 }}>
            Widen the selection, or preview the full report with demo data.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            {hasFilters && (
              <button onClick={clearFilters} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 9, border: 'none', cursor: 'pointer', background: GREEN, color: '#fff', fontSize: 14, fontWeight: 700 }}>
                <FilterX size={16} />Clear all filters
              </button>
            )}
            <button onClick={() => set({ mode: 'demo' })} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 9, border: `1.5px solid ${PURPLE}`, cursor: 'pointer', background: '#fff', color: PURPLE, fontSize: 14, fontWeight: 700 }}>
              <Eye size={16} />Preview with demo data
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 16, marginBottom: 24 }}>
            <Kpi label={sel.metric === 'expansion' ? 'Top expansion target' : 'Top area to focus'} value={hotAreas[0]?.area || '—'} sub={hotAreas[0] ? (sel.metric === 'expansion' ? `market ${hotAreas[0].marketIndex} · score ${fmtVal(hotAreas[0].value, sel.metric)}` : `${fmtVal(hotAreas[0].value, sel.metric)} ${metricLabel}`) : 'no activity yet'} color={RED} icon={<Flame size={20} />} />
            {sel.metric === 'expansion'
              ? <Kpi label="Platform traction" value={(totals.matchedDemands || 0).toLocaleString('en-IN')} sub={`demands so far · ${totals.areas || 0} markets ranked`} color={GREEN} icon={<MapPin size={20} />} />
              : <Kpi label={`Total ${metricLabel}`} value={fmtVal(totals.current || 0, sel.metric)} sub={`${totals.areas || 0} ${sel.level === 'city' ? 'cities' : 'states'} with activity`} color={GREEN} icon={<MapPin size={20} />} />}
            <Kpi
              label={meta.forecastMonths > 0 ? `Projected next ${meta.forecastMonths} mo` : 'Projection'}
              value={meta.forecastMonths > 0 ? fmtVal(fcVals.reduce((s, r) => s + r.value, 0), sel.metric) : '—'}
              sub={meta.forecastMonths > 0
                ? (growth === null ? 'indicative — history is short' : `${growth >= 0 ? '▲' : '▼'} ${Math.abs(growth)}% vs last ${cmpLen} mo`)
                : 'needs 3+ months of history'}
              color={PURPLE} icon={<TrendingUp size={20} />} />
            <Kpi label="Data coverage" value={`${dataMonths} mo`} sub={meta.dataStart ? `${meta.dataStart} – ${meta.dataEnd}` : 'no data in window'} color={BLUE} icon={<Info size={20} />} />
          </div>

          {/* Trend chart */}
          <div className="card" style={{ padding: 28, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 700, color: CHAR, margin: 0 }}>
                {sel.metric === 'expansion' ? 'Platform buyer demand — monthly trend' : `${metricLabel} — monthly trend`}{meta.forecastMonths > 0 ? ` + ${meta.forecastMonths}-mo projection` : ''}
              </h3>
              <div style={{ display: 'flex', gap: 16, fontSize: 12, color: MUTED, fontWeight: 600 }}>
                <span><span style={{ display: 'inline-block', width: 14, height: 3, background: GREEN, verticalAlign: 3, marginRight: 5 }} />Actual</span>
                {meta.forecastMonths > 0 && <span><span style={{ display: 'inline-block', width: 14, height: 0, borderTop: `3px dashed ${PURPLE}`, verticalAlign: 3, marginRight: 5 }} />Projection</span>}
              </div>
            </div>
            <TrendChart series={series} />
            {meta.forecastMonths > 0 && dataMonths < 12 && (
              <p style={{ fontSize: 11.5, color: MUTED, margin: '10px 0 0', fontWeight: 500 }}>
                ⚠ Projection is based on only {dataMonths} months of history — treat it as a direction, not a number. It sharpens automatically as months accrue.
              </p>
            )}
          </div>

          {/* Geo heat ranking */}
          <div className="card" style={{ padding: 28 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 8 }}>
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 700, color: CHAR, margin: 0 }}>
                {sel.level === 'city' ? (sel.state ? `${sel.state} — cities` : 'Top cities') : 'States'} by {metricLabel}
              </h3>
              <div style={{ display: 'flex', gap: 14, fontSize: 12, fontWeight: 600 }}>
                <Legend c={RED} t="Hot — focus" /><Legend c={GOLD} t="Warm" /><Legend c={GREEN} t="Cool" />
              </div>
            </div>
            {sel.level === 'state' && <p style={{ fontSize: 12.5, color: MUTED, margin: '0 0 18px', fontWeight: 500 }}>Click any state to drill into its cities.</p>}
            {geo.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '50px 20px', color: MUTED }}>
                <MapPin size={36} style={{ opacity: 0.3 }} /><p style={{ fontSize: 15, fontWeight: 500, marginTop: 10 }}>No geographic activity for this selection.</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {geo.slice(0, 20).map((g, i) => {
                  const pct = Math.max(2, (g.value / maxVal) * 100);
                  const col = HEAT[g.band] || BLUE;
                  return (
                    <div key={g.area} onClick={() => drillCity(g.area)} title={sel.level === 'state' ? `Drill into ${g.area}` : g.area}
                      style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 14px', borderRadius: 10, background: HEAT_BG[g.band] || '#f9fafb', border: `1px solid ${col}22`, cursor: sel.level === 'state' ? 'pointer' : 'default' }}>
                      <div style={{ width: 22, fontSize: 13, fontWeight: 700, color: MUTED }}>{i + 1}</div>
                      <div style={{ width: 150, fontSize: 14.5, fontWeight: 700, color: CHAR, display: 'flex', alignItems: 'center', gap: 6 }}>
                        {g.band === 'hot' && <Flame size={15} style={{ color: RED }} />}{g.area}
                      </div>
                      <div style={{ flex: 1, height: 22, background: '#fff', borderRadius: 6, overflow: 'hidden', border: `1px solid ${BORDER}` }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg, ${col}cc, ${col})`, borderRadius: 6, transition: 'width .7s cubic-bezier(.4,0,.2,1)' }} />
                      </div>
                      <div style={{ width: 90, textAlign: 'right', fontSize: 14, fontWeight: 700, color: col }}>{fmtVal(g.value, sel.metric)}</div>
                      <div style={{ width: sel.metric === 'expansion' ? 210 : 170, fontSize: 11.5, color: MUTED, textAlign: 'right' }}>
                        {sel.metric === 'expansion' ? `market ${g.marketIndex} · ` : ''}{kfmt(g.demand)} demands · {kfmt(g.vendors)} vendors{g.revenue > 0 ? ` · ₹${kfmt(g.revenue)}` : ''}
                      </div>
                      {sel.level === 'state' && <ChevronRight size={16} style={{ color: MUTED }} />}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Focus recommendations */}
          {hotAreas.length > 0 && (
            <div className="card" style={{ padding: 28, marginTop: 24 }}>
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 700, color: CHAR, margin: '0 0 4px' }}>
                <Flame size={20} style={{ verticalAlign: -3, marginRight: 6, color: RED }} />Where to focus sales &amp; lead-gen
              </h3>
              <p style={{ fontSize: 13, color: MUTED, margin: '0 0 20px', fontWeight: 500 }}>Ranked opportunities for {scopeText}.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
                {hotAreas.map((h, i) => (
                  <div key={h.area} style={{ padding: 16, borderRadius: 12, background: HEAT_BG[h.band], borderLeft: `4px solid ${HEAT[h.band]}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontSize: 15, fontWeight: 800, color: CHAR }}>#{i + 1} {h.area}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: HEAT[h.band], textTransform: 'uppercase', letterSpacing: 0.5 }}>{h.band}</span>
                    </div>
                    <div style={{ fontSize: 13.5, color: CHAR, lineHeight: 1.5 }}>{h.action}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <p style={{ fontSize: 11.5, color: MUTED, marginTop: 20, textAlign: 'center' }}>
        Opportunity = buyer demand ÷ (active vendors + 1). Expansion = market index × 10 ÷ (1 + vendors); the market index is a static GSDP/consumption ESTIMATE, not live spend data.
        {meta.generatedAt ? ` Generated ${new Date(meta.generatedAt).toLocaleString('en-IN')}.` : ''} {sel.mode === 'demo' ? 'Demo figures are synthetic.' : ''}
      </p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</label>
      {children}
    </div>
  );
}
function Kpi({ label, value, sub, color, icon }) {
  return (
    <div className="card" style={{ padding: 20, borderTop: `4px solid ${color}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: 11.5, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.7, fontWeight: 700 }}>{label}</div>
        <span style={{ color, opacity: 0.85 }}>{icon}</span>
      </div>
      <div style={{ fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 800, color: CHAR, margin: '8px 0 3px', wordBreak: 'break-word' }}>{value}</div>
      <div style={{ fontSize: 12.5, color, fontWeight: 600 }}>{sub}</div>
    </div>
  );
}
function Legend({ c, t }) {
  return <span style={{ color: MUTED }}><span style={{ display: 'inline-block', width: 11, height: 11, borderRadius: 3, background: c, verticalAlign: -1, marginRight: 5 }} />{t}</span>;
}
